/**
 * Getting a photo out of the phone and into a shot slot.
 *
 * The camera itself is a `<input type="file">`; there is no other way to reach
 * an iPhone camera from a web app, and it is the right one — iOS shows its own
 * capture UI, the engineer sees a full-quality preview, and nothing about the
 * page can drop the photo on the way.
 *
 * Two details here are iOS, not preference:
 *
 *  - **`capture="environment"`** opens the rear camera directly and removes the
 *    photo-library option. Good for the normal path and wrong for the stamping
 *    camera the engineers already use, so both modes exist and the slot chooses.
 *  - **The input's value must be cleared after every pick.** Safari fires no
 *    `change` when the same file is chosen twice, so a retake of an identical
 *    shot would silently do nothing.
 */

import { DEFAULT_QUALITY, preparePhoto, type ImagePipeline, type PhotoQuality } from './photo.js';

export type CaptureMode = 'camera' | 'library';

export interface CameraAttributes {
  accept: string;
  /** Absent for a library pick: present-but-empty still forces the camera in Safari. */
  capture?: 'environment';
  multiple?: boolean;
}

export function cameraAttributes(mode: CaptureMode): CameraAttributes {
  // `image/*` rather than a list of types: an iPhone hands back HEIC, an Android
  // JPEG or WEBP, and a narrow accept list silently greys out the camera on
  // whichever device was not thought of.
  return mode === 'camera' ? { accept: 'image/*', capture: 'environment' } : { accept: 'image/*' };
}

export interface Picked {
  blob: Blob;
  /** The name the phone gave it, kept only for diagnosis — never for filing. */
  sourceName: string;
  compressed: boolean;
  /** Why it was stored as-is, when it was. */
  reason?: string;
  originalBytes: number;
}

export class CameraError extends Error {}

/**
 * Turns what the file input handed back into what we store.
 *
 * A picked file is checked before anything else touches it, because every later
 * step — the queue, the upload, the slot turning green — assumes there are bytes
 * behind it.
 */
export async function acceptPick(
  files: FileList | readonly File[] | null,
  pipeline: ImagePipeline,
  q: PhotoQuality = DEFAULT_QUALITY,
): Promise<Picked> {
  const list = files ? Array.from(files as ArrayLike<File>) : [];
  // A cancelled picker fires `change` with nothing. That is not an error and
  // must not blank an existing photo.
  if (list.length === 0) throw new CameraError('no photo was chosen');

  const file = list[0]!;
  if (file.size === 0) throw new CameraError('the camera returned an empty file');
  if (file.type && !/^image\//i.test(file.type)) {
    // Files app lets an engineer pick a PDF. Uploading it would tick the slot
    // green with something no report can place.
    throw new CameraError(`${file.name || 'that file'} is not a photo`);
  }

  const prepared = await preparePhoto(file, pipeline, q);
  return {
    blob: prepared.blob,
    sourceName: file.name || 'photo',
    compressed: prepared.compressed,
    ...(prepared.reason ? { reason: prepared.reason } : {}),
    originalBytes: file.size,
  };
}

/** What the engineer is told after a pick, when anything worth saying happened. */
export function pickNote(p: Picked): string | undefined {
  if (p.compressed) return undefined; // the normal path says nothing
  if (p.reason?.startsWith('stored uncompressed')) {
    // Honest rather than silent: the photo is safe, the phone is carrying more
    // weight than planned, and that matters at 400 MB.
    return 'Saved at full size — this phone could not resize it.';
  }
  return undefined;
}
